import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import {
    Button, Card, Toggle, Alert, Spinner,
} from './shared';

const RESOURCES = ['skills', 'subagents'];

const AdminSync = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState({});
    const [filesResult, setFilesResult] = useState(null);
    const [s3Result, setS3Result] = useState(null);
    const [forceSync, setForceSync] = useState(false);
    const [error, setError] = useState(null);
    const [migrating, setMigrating] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);

    const fetchStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getSyncStatus();
            setStatus(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch sync status');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStatus(); }, []);

    const handleSyncFilesAll = async () => {
        setSyncing(prev => ({ ...prev, files_all: true }));
        setFilesResult(null);
        try {
            const data = await adminApi.syncFilesAll(forceSync);
            setFilesResult({ type: 'all', ...data });
            fetchStatus();
        } catch (err) {
            setError(err.message || 'Failed to sync files to DB');
        } finally {
            setSyncing(prev => ({ ...prev, files_all: false }));
        }
    };

    const handleSyncS3All = async () => {
        setSyncing(prev => ({ ...prev, s3_all: true }));
        setS3Result(null);
        try {
            const data = await adminApi.syncS3All();
            setS3Result({ type: 'all', ...data });
            fetchStatus();
        } catch (err) {
            setError(err.message || 'Failed to sync DB to S3');
        } finally {
            setSyncing(prev => ({ ...prev, s3_all: false }));
        }
    };

    const handleSyncFiles = async (resource) => {
        const key = `files_${resource}`;
        setSyncing(prev => ({ ...prev, [key]: true }));
        setFilesResult(null);
        try {
            const apiMethod = {
                skills: adminApi.syncFilesSkills,
                subagents: adminApi.syncFilesSubagents,
            }[resource];
            const data = await apiMethod(forceSync);
            setFilesResult({ type: resource, ...data });
            fetchStatus();
        } catch (err) {
            setError(err.message || `Failed to sync ${resource} from files`);
        } finally {
            setSyncing(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleSyncS3 = async (resource) => {
        const key = `s3_${resource}`;
        setSyncing(prev => ({ ...prev, [key]: true }));
        setS3Result(null);
        try {
            const apiMethod = {
                skills: adminApi.syncS3Skills,
                subagents: adminApi.syncS3Subagents,
            }[resource];
            const data = await apiMethod();
            setS3Result({ type: resource, ...data });
            fetchStatus();
        } catch (err) {
            setError(err.message || `Failed to sync ${resource} to S3`);
        } finally {
            setSyncing(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleRunMigrations = async () => {
        setMigrating(true);
        setMigrationResult(null);
        setError(null);
        try {
            const data = await adminApi.runMigrations();
            setMigrationResult(data);
            if (data.success) fetchStatus();
        } catch (err) {
            setError(err.message || 'Failed to run migrations');
        } finally {
            setMigrating(false);
        }
    };

    const handleRunSeeds = async () => {
        setSeeding(true);
        setSeedResult(null);
        setError(null);
        try {
            const data = await adminApi.runSeeds();
            setSeedResult(data);
        } catch (err) {
            setError(err.message || 'Failed to run seeds');
        } finally {
            setSeeding(false);
        }
    };

    const isSyncing = Object.values(syncing).some(Boolean);

    if (loading) {
        return <Spinner size="lg" center text="Loading sync status..." />;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Data Sync</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Two-phase sync: Files → DB → S3</p>
                </div>
                <Button variant="secondary" size="sm" onClick={fetchStatus}>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                    </svg>
                    Refresh
                </Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
            {migrationResult && (
                <Alert variant={migrationResult.success ? 'success' : 'error'}>
                    {migrationResult.message}
                </Alert>
            )}
            {seedResult && (
                <Alert variant={seedResult.success ? 'success' : 'error'} onClose={() => setSeedResult(null)}>
                    {seedResult.success
                        ? `Seeds completed: ${Object.entries(seedResult.results || {}).map(([k, v]) => `${k} (${v})`).join(', ')}`
                        : (seedResult.message || 'Seed failed')
                    }
                </Alert>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="Database Migrations" headerRight={
                    <Button size="sm" loading={migrating} onClick={handleRunMigrations}>
                        Run Migrations
                    </Button>
                }>
                    <p className="text-xs text-gray-500">Run pending database migrations to update schema</p>
                </Card>

                <Card title="Seed Data" headerRight={
                    <Button size="sm" loading={seeding} onClick={handleRunSeeds}>
                        Run Seeds
                    </Button>
                }>
                    <p className="text-xs text-gray-500">Insert default data (tiers, configs, model costs)</p>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {RESOURCES.map(resource => {
                    const data = status?.[resource];
                    if (!data) return null;
                    const total = data.total || 0;
                    const synced = data.synced_to_s3 || 0;
                    const pct = total > 0 ? Math.round((synced / total) * 100) : 0;

                    return (
                        <Card key={resource} title={resource.charAt(0).toUpperCase() + resource.slice(1)}>
                            <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Total</span>
                                    <span className="font-medium text-gray-900">{total}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Built-in</span>
                                    <span className="font-medium text-gray-900">{data.builtin || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Synced to S3</span>
                                    <span className="font-medium text-emerald-600">{synced}</span>
                                </div>
                            </div>
                            <div className="pt-2">
                                <div className="flex justify-between text-xs text-gray-400 mb-1">
                                    <span>S3 Progress</span>
                                    <span>{pct}%</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div
                                        className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : 'bg-gray-900'}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Card noPadding>
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${status?.s3_enabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="text-sm font-medium text-gray-700">
                                {status?.s3_enabled ? 'S3 Enabled' : 'S3 Disabled'}
                            </span>
                        </div>
                        <Toggle
                            label="Force override (Files → DB)"
                            checked={forceSync}
                            onChange={setForceSync}
                            size="sm"
                        />
                    </div>
                    {!status?.s3_enabled && (
                        <span className="text-xs text-amber-600">Set S3_BUCKET_NAME to enable</span>
                    )}
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Phase 1: Files → DB" description="Load resources from file system to database">
                    <Button
                        className="w-full"
                        loading={syncing.files_all}
                        disabled={isSyncing}
                        onClick={handleSyncFilesAll}
                    >
                        Sync All Files → DB
                    </Button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {RESOURCES.map(resource => (
                            <Button
                                key={resource}
                                variant="secondary"
                                size="sm"
                                loading={syncing[`files_${resource}`]}
                                disabled={isSyncing}
                                onClick={() => handleSyncFiles(resource)}
                                className="capitalize"
                            >
                                {resource}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card title="Phase 2: DB → S3" description="Backup database to S3 storage">
                    <Button
                        className="w-full"
                        loading={syncing.s3_all}
                        disabled={isSyncing || !status?.s3_enabled}
                        onClick={handleSyncS3All}
                    >
                        Sync All DB → S3
                    </Button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {RESOURCES.map(resource => (
                            <Button
                                key={resource}
                                variant="secondary"
                                size="sm"
                                loading={syncing[`s3_${resource}`]}
                                disabled={isSyncing || !status?.s3_enabled}
                                onClick={() => handleSyncS3(resource)}
                                className="capitalize"
                            >
                                {resource}
                            </Button>
                        ))}
                    </div>
                </Card>
            </div>

            <SyncResultCard title="Files → DB" result={filesResult} type="files" />
            <SyncResultCard title="DB → S3" result={s3Result} type="s3" />
        </div>
    );
};

function SyncResultCard({ title, result, type }) {
    if (!result) return null;

    if (result.type === 'all') {
        return (
            <Card title={`${title} Results`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {RESOURCES.map(resource => {
                        const data = result[resource];
                        if (!data) return null;
                        return (
                            <div key={resource} className="p-3 bg-gray-50 rounded-md">
                                <p className="text-sm font-medium text-gray-700 mb-1.5 capitalize">{resource}</p>
                                <div className="text-xs space-y-0.5">
                                    <p className="text-emerald-600">Synced: {data.synced?.length || 0}</p>
                                    {type === 'files' && <p className="text-gray-500">Skipped: {data.skipped?.length || 0}</p>}
                                    {type === 's3' && <p className="text-amber-600">Deleted: {data.deleted?.length || 0}</p>}
                                    <p className="text-red-600">Errors: {data.errors?.length || 0}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
        );
    }

    const { synced = [], skipped = [], deleted = [], errors = [] } = result;
    const label = result.type.charAt(0).toUpperCase() + result.type.slice(1);

    return (
        <Card title={`${title}: ${label}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {synced.length > 0 && (
                    <ResultList items={synced} label="Synced" variant="success" />
                )}
                {skipped.length > 0 && (
                    <ResultList items={skipped} label="Skipped" variant="default" />
                )}
                {deleted.length > 0 && (
                    <ResultList items={deleted} label="Deleted" variant="warning" />
                )}
                {errors.length > 0 && (
                    <div className="p-3 bg-red-50 rounded-md">
                        <p className="text-xs font-medium text-red-800 mb-1">Errors ({errors.length})</p>
                        <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                            {errors.map((err, idx) => (
                                <li key={idx} className="truncate" title={err.error}>
                                    {err.id}: {err.error}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Card>
    );
}

function ResultList({ items, label, variant }) {
    const colorMap = {
        success: { bg: 'bg-emerald-50', title: 'text-emerald-800', text: 'text-emerald-700' },
        warning: { bg: 'bg-amber-50', title: 'text-amber-800', text: 'text-amber-700' },
        default: { bg: 'bg-gray-50', title: 'text-gray-700', text: 'text-gray-600' },
    };
    const colors = colorMap[variant] || colorMap.default;

    return (
        <div className={`p-3 ${colors.bg} rounded-md`}>
            <p className={`text-xs font-medium ${colors.title} mb-1`}>{label} ({items.length})</p>
            <ul className={`text-xs ${colors.text} space-y-0.5 max-h-32 overflow-y-auto`}>
                {items.map(id => <li key={id} className="truncate">{id}</li>)}
            </ul>
        </div>
    );
}

export default AdminSync;
